import React from 'react';
import { Volume2, Info, ChevronRight, Home, Castle, Mail } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface RobertsPart2SectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const RobertsPart2Section: React.FC<RobertsPart2SectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 text-[#2c221e] font-sans">
      {/* Top Breadcrumb / Page Header */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold">
            {onSelectPage && (
              <button
                onClick={() => onSelectPage('hub')}
                className="hover:underline flex items-center gap-1 text-[#7a5b3a] cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                <span>The Roberts Family</span>
              </button>
            )}
            {onSelectPage && <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />}
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full uppercase tracking-wider text-[11px] border border-[#d8ceb9]">
              Part 2 of 5
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={speakCurrentPage}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
              title="Listen to Part 2: Large, Literate, Literary and Hardworking"
            >
              <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
              <span>{isPlaying ? 'Listening to Part 2...' : 'Listen to Part 2'}</span>
            </button>
          </div>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          THE ROBERTS FAMILY WAS LARGE, LITERATE, LITERARY AND HARDWORKING
        </h1>
        <p className="font-serif text-base sm:text-lg text-[#7a5b3a] italic leading-relaxed">
          Cabinet maker, seaman, road builder, gold miner, bridge maker, storekeeper, inn keeper, hotel operator, drapery salesman, railway construction worker, farm worker, farmer
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Section 1: Overview and Looking Back */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight border-b-2 border-[#d8ceb9] pb-2">
            AN OVERVIEW<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-1">1</a></sup> and looking back
          </h2>

          <p className="font-medium text-[#4a3525] italic">
            Cabinet maker, seaman, road builder, gold miner, bridge maker, storekeeper, inn keeper, hotel operator, drapery salesman, railway construction worker, farm worker, farmer
          </p>

          {/* Image 182 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/182 Robert & Sarah Roberts 1916.jpg"
              alt="Robert & Sarah Roberts 1916"
              caption="(Photo supplied by David Fordyce)"
              filenameHint="182 Robert & Sarah Roberts 1916.jpg"
            />
          </figure>

          <p>
            Robert, although an Englishman by birth, was Welsh by upbringing. He lived with his grandparents at Penmaenmawr for five years, until after his seventh birthday, and for a little time while there attended a Dame School. On his return to Daventry, southeast of Birmingham, he was enrolled at a local grammar school and found himself a "foreigner" speaking a different language.
          </p>

          <p>
            He finished school at 12 and was apprenticed to a cabinetmaker but the constant standing affected his legs and he left after three months.
          </p>

          <p>
            Aged 16 he was apprenticed to sea. His Ordinary Apprentice's Indenture, thirteenth day of March 1858, was made between Robert Morris Roberts aged 16, a native of Daventry in the county of Northampton..."of the first part Edmund Graham in the county of Northumberland of the second part, and Edward Roberts of Daventry in the county of Northampton WITNESSETH, That Robert Morris Roberts hereby voluntarily binds himself unto the said Edmund Graham...for the term of Four years."
          </p>

          <p>
            A childhood friend who played with Robert and his sisters, Eliza and Clara, wrote years later that one day he found them crying because Edward, considered a "hard man", had told his son that he was to go to the Man-O-Wars (meaning then navy, or to go to sea). That decision was his father's choice but it had consequences perhaps he never foresaw.
          </p>

          <p>
            The indenture was registered at the Port of Shields, Newcastle on 16 March 1857. His first voyage was a 12 month mission to sail to India and bring home British soldiers after the Indian Mutiny of 1857-58 against British rule in India. He served mainly in the India trade on two ships before serving on the Brisk. On that first voyage he learned to swim in the warm waters of Mauritius. He would have seen some exotic sights in the West Indies and other ports of call and experienced tropical weather and temperatures. By contrast, he also worked small ships carrying Welsh slate, and coastal boats taking coal from Newcastle, where he said he couldn't understand a word the men loading the coal spoke!<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-3">3</a></sup>
          </p>

          <p>
            He returned from one voyage with tar- stained hands, so his mother made him wear white gloves when the family went to hear the extraordinary Baptist preacher Charles Spurgeon. His family were Wesleyan Chapel people, and his father was a Sunday School Superintendent. They were strictly against alcohol. The contrast between home life and living on board a ship must have been dramatic at times.
          </p>

          <p>
            He first came to Nelson from Melbourne on 11 April 1862, and for the next three months the small ship carried cargoes of cattle, crates of apples and passengers between Wellington and Lyttelton. His legally binding apprenticeship had expired by the time the Brisk reached Nelson.
          </p>

          <p>
            His first job on land was working on the main drain from Kaiapoi to Rangiora, which is where he likely met William Alexander, a former seaman turned entrepreneur and beverage maker, and who was to be an influential figure in his life for the next few years.
          </p>

          <p>
            Robert was back in Kaiapoi in 1864 and by the end of that year he was running a very basic wayside "inn" at the Waiapara River, on the access route to the West Coast gold diggings via the Hurunui River, Lake Sumner and over Harper's pass to the Taramakau River to where gold had been discovered. He served up Alexander's alcohol products, but he did not have a licence to do so and soon the constabulary was on the case. Robert got away, hidden under a load of hay for some of the journey to Leithfield then Christchurch.
          </p>

          <p>
            He took off down to the Otago. In early 1864 and early 1865 he was working as a cook on a sheep station in the high country at Hakataramea or Station Peak. He probably ventured onto the goldfields again, selling stores or supplies. Pictured below - the Hakataramea River and Valley.
          </p>

          <p>
            In March or April 1865, he sailed from Dunedin to Brisbane to visit his brother, Thomas Parbery Roberts, who had immigrated to Queensland, later farming at Springsure, 765km northwest of Brisbane. The two brothers worked in a group surveying a rail route north of Rockhampton. Thomas's home in 1871 is pictured.
          </p>

          {/* Image 183 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/183 Tom Roberts_ house in Queensland.jpg"
              alt="Tom Roberts house in Queensland"
              caption="Tom Roberts house in Queensland"
              filenameHint="183 Tom Roberts_ house in Queensland.jpg"
            />
          </figure>

          <p>
            In October 1865 he sailed to Hokitika in response to an offer of work from William Alexander, at his accommodation house ventures in the Taramakau Valley. Robert took over the management of the Junction House and renewed his acquaintance with the young Irish maid he had met in Christchurch and comforted on the back of the buggy as she was driven away to work for Mrs Alexander in Kaiapoi.
          </p>

          <p>
            The following year Robert and Sarah married and worked together at Taramakau. After spending ten years in Westland he and his family returned to Canterbury and three years later (in 1878) established themselves on 59 acres of land at Cabbage Tree Flat. Sometime during the three years that the family were residing at Ashley and Leithfield Robert joined the International Order of Good Templars.
          </p>

          <p>
            In 1890 this small farm, which lay behind where the Motunau Garage on the Main North Road now stands, was sold and the property now known as Plas Isa in the Waikari Valley purchased. The family did not immediately settle on this farm of 343 acres but moved to a small holding of 70 acres out at Happy Valley. Robert had this farm rent free for four years to clear it of gorse and broom and for another two at a small rental. During this time the Waikari Valley farm was leased and in 1896 the family finally entered into possession. And thus began their pathway to a more prosperous and secure future - Robert was 38, Sarah 31.
          </p>

          <p>
            The Brisk that Robert voyaged in had a colourful and fascinating history.
          </p>

          {/* Image 184 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/184 Brisk boat.jpg"
              alt="Brisk boat"
              caption="Brisk launched in 1851, 10.7 m beam. She served in the Crimean War, on West African anti-piracy patrols, and was in the campaign to stop slave boats, and carried troops for the NZ Wars, helped search for Dr Livingstone (1854) along the Kongone River and brought out emigrants. On 10 August 1860 Brisk captured the Manuella in the Mozambique Channel, which was carrying 800 slaves."
              filenameHint="184 Brisk boat.jpg"
            />
          </figure>
        </section>

        {/* Section 2: MRS SARAH ELIZABETH ROBERTS by her son Edward Roberts (1952) */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            MRS SARAH ELIZABETH ROBERTS by her son Edward Roberts (1952)<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-4">4</a></sup>
          </h2>
          <h3 className="font-serif text-xl font-bold text-[#7a5b3a] italic">
            Some Memories of the 'Sixties
          </h3>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "Vivid memories of life on the old West Coast are retained by Mrs Sarah Elizabeth Roberts, of Bealey Street, St Albans<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-5">5</a></sup>, who celebrates her eighty-sixth birthday this month. She first knew it in the heyday of its prosperity, at the time when men were beckoned to the rich diggings at the Greenstone, the Waimea, and Hokitika by "a finger dipped in gold," For 10 years she lived beside the road winding through the Otira and Teremakau Valleys, along which passed the motley pageant of the 'sixties, and she distinctly remembers many of the travellers. One man who greatly impressed her was Sir George Grey, who went through in January 1867, accompanied by several Maoris.
            </p>

            <p>
              Mrs Roberts was born in 1849 at Magheraliin, County Down, Ireland. In 1864, with her mother and stepfather she set sail for New Zealand in the British Empire, which arrived at Port Cooper, as Lyttelton was still known, on September 6, after a voyage of 98 days. There was great excitement in town on the day of the British Empire's arrival, as she was the biggest vessel which had ever entered the port - her burthen was 2600 tons, and she was said to be a former troopship, in use during the Crimean War. Bad luck seems to have attended most of her voyages, and she was finally wrecked in a tidal wave at St. Thomas, West Indies.
            </p>
          </blockquote>

          {/* Image 185 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/185 Sarah sailed to NZ on the British Empire.jpg"
              alt="Sarah sailed to NZ on the British Empire"
              caption="Sarah sailed to NZ on the British Empire"
              filenameHint="185 Sarah sailed to NZ on the British Empire.jpg"
            />
          </figure>

          <p>
            Beyond meeting with obstinate winds which actually carried her to the west of Trinidad, her single voyage to Lyttelton was a pleasant one. Few gales were encountered, and the death-roll of 15 was held to be so small that Henry Selfe, who had just replaced John Marshman as the Canterbury Association's immigration agent on London, was congratulated on the excellent conditions under which the 366 immigrants had been sent out.
          </p>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              The Teremakau House
            </h3>
            <p>
              After living at Kaiapoi for a short time, the young Irish girl went to the West Coast, where she was employed by Mrs. Alexander, whose husband was part-owner of both the upper and lower Teremakau accommodation houses. Mount Alexander, the giant peak of the Teremakau Valley, is named after this pioneer.
            </p>
            <p>
              At the age of 17 Mrs Roberts was married to Mr Robert Morris Roberts, the ceremony taking place in St Peter's, Upper Riccarton, and the bridal driving in from Yaldhurst to the church in a high dogcart. Since both bride and bridesmaid wore the fashionable crinolines of the period, Mrs Roberts recalls that the dogcart was somewhat overcrowded.
            </p>
            <p>
              Returning to the Coast, the young couple took over the upper Teremakau house. Mrs Roberts told an interviewer that travellers who journey down the valley today can have little idea of its wild, unspoiled beauty during the 'sixties. Pigeons could be shot from the door of the house, paradise and grey duck were plentiful along the river, while the chorus of tuis and bellbirds at dawn and dusk were something never to be forgotten. Black teal were always to be obtained from Rotomanu, further down the valley.
            </p>
            <p>
              In one shooting expedition to this pretty little lake Mrs. Roberts discovered that the bag of shot which had been attached to the saddle of their packhorse had dropped off somewhere on the journey. Determined not to have made the trip in vain, Mr. Roberts loaded his gun with fine gravel and shot the teal with that.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              High transport costs
            </h3>
            <p>
              A condition of the Teremakau license was that all travellers who so desired must be ferried across the Teremakau River: so many of the early gold-seekers had been drowned in this river that it had become known as the "diggers' grave." Meals must also be available to the coachloads of passengers passing up and down the valley. Needless to say, Mrs Robert's kitchen arrangements were very primitive, and at no time on the Coast did she have anything other than a colonial oven for her bread-making and cooking. Meat was sometimes unprocurable. Fruit, the pioneers never saw, and green vegetables but seldom. As a substitute for the latter they boiled the young, curled fonds of ferns, a dish they called "pick-a-pick."
            </p>
            <p>
              Mrs Roberts remarked that travellers today would be astonished by the cost of transport and accommodation on the old West Coast. The simplest meals could not be procured for less than half a crown. The return fare from the Teremakau to Christchurch was 10 Pounds, and to Hokitika 2 Pounds 10 shillings.
            </p>
            <blockquote className="bg-[#f3ede0] p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base italic text-[#2c221e] space-y-3">
              <p>
                "Naturally this prevented our travelling very often," said Mrs Roberts. "Such journeys as we did make were sometimes rather exciting, I have crossed the Waimakariri with the horses all but swimming, and the water pouring in on the floor of the swaying coach,"
              </p>
              <p>
                On another trip eastward, slips in the Otira Gorge prevented the coaches from getting through, and Mrs Roberts was compelled to ride, holding an infant on the saddle while her husband followed behind with another child. The westward journey through the gorge was invariably and adventurous and uncomfortable one, for after leaving the zig-zag, the coaches dropped down into the Otira riverbed, and rattled their way over the boulders and shingle for several miles.
              </p>
            </blockquote>
          </div>

          {/* Image 186 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186 1868 Otira_s infamous Zig Zag.jpg"
              alt="Zig Zag Road, Otira 1868"
              caption="Zig Zag Road, Otira 1868"
              filenameHint="186 1868 Otira_s infamous Zig Zag.jpg"
            />
          </figure>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              A New Start
            </h3>
            <p>
              Mr Roberts finally sold the Teremakau house to Mr Jackson, whose name afterwards became a fixture in the locality. Nowadays the express trains stop at Jackson's before crossing the Teremakau. The house which then stood near the river was later washed away in a big flood, and its successor was built high on a terrace. Floods also demolished the Otira Gorge house, which Mr Roberts took over after relinquishing the Teremakau place. Its one- time site is marked by a cairn far out in the Otira riverbed.
            </p>
            <p>
              In the 'seventies the Coast suffered from a severe depression. The "easy" gold had been worked out, and people began to drift away to other parts of New Zealand. Few people travelled across the Alps, and accommodation houses suffered correspondingly.
            </p>
            <p>
              Finding it impossible to carry on, Mr and Mrs Roberts decided to make a fresh start in Canterbury. The young wife and four small children travelled by coach, but lack of funds compelled Mr Roberts to walk from Otira to Christchurch. Soon after they quitted to Coast the discovery of the Kumara goldfield ushered in a new period of prosperity in Westland, but by that time Mr Roberts was working on railway construction in Canterbury.
            </p>
            <p>
              After experiencing many vicissitudes, Mr and Mrs Roberts took up land in Motunau, and in 1890 they bought a farm in the Waikari Valley. This land, together with the original farm, is now occupied by the eldest son<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-6">6</a></sup>. Mr and Mrs Roberts retired in 1908, buying a property at St. Albans<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-7">7</a></sup>, where Mr Roberts died 14 years ago.
            </p>
            <p>
              Mrs Roberts has nine surviving children, 37 grandchildren and 23 great-grandchildren."
            </p>
          </div>
        </section>

        {/* Section 3: TARAMAKAU ACCOMMODATION HOUSE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            TARAMAKAU ACCOMMODATION HOUSE - the original hotel at Jacksons on the western side of Otira
          </h2>
          <h3 className="font-serif text-lg font-bold text-[#7a5b3a]">
            The Innkeepers - Robert Morris and Sarah Elizabeth Roberts, nee Baxter
          </h3>

          <p>
            Imagine this, 1876, Cobb & Co stagecoach passengers arriving after the hazardous crossings and rough tracks of Arthur's Pass, hungry and ready for steady ground again. Travellers alighted, ready to press onward by taking the overhead ferry across the Taramakau River to go on to Lake Brunner or gold digging prosects further afield. The accommodation house was in the vicinity of the present Jacksons Hotel.
          </p>

          <p>
            Robert and Sarah Morris owned this accommodation house for a short time before selling to buy land at Motunau in 1878, and then Plas Isa in Woods Road/Waikari Valley (1890). Sarah had worked for the previous owners<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-8">8</a></sup> after arriving in NZ in 1864. Robert had a shop after some years of gold prospecting in Otago and Westland. After their marriage on 24 December 1866, they took over the management of this accommodation house which required the owners to provide meals and a ferry service.
          </p>

          {/* Image 186A */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186A Accommodation House near Otira (Photo Couresy Alexannder Turnbull Library).jpg"
              alt="Accommodation House near Otira"
              caption="Robert and Sarah in front of the accommodation house they ran from 1872-74 (Photo: Courtesy Alexander Turnbull Library)"
              filenameHint="186A Accommodation House near Otira (Photo Couresy Alexannder Turnbull Library).jpg"
            />
          </figure>

          <p>
            In his book "Beyond the Hills of Hundalee" their son T.E.L. Roberts recalled those early days.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "As a babe who was introduced to terra firma on the Canterbury side of the Southern Alps, the writer was being carried down the zig-zag of Otira Gorge when the steed stumbled, precipitating his rider and his baby bundle over that cliff edge, fortunately at a point where recovery was possible.
            </p>

            <p>
              Consternation of course pictured calamity for the first baby boy, as he was thrown clear away in the fall. Hurried investigation, however, revealed that the little man was still tight in his wraps, minus even a scratch, nor was there a squeak out of him.
            </p>

            <p>
              His childhood was spent as the base of the hill, where long the old hostel stood near the river, as was the custom in those early days of ferrying travellers across. The old house acted too as a changing station for the coaching horses on the run through to Hokitika. What a run that was in those palmy days of the Golden West, when the road, not long formed, ran through virgin forest that knew scarce a break, save a clearing for a changing station say every 20 miles.
            </p>

            <p>
              Those of us who can still remember the beauty of the bush, with its teeming bird life, can only sigh now for the glory that has gone, to more or less give place to weeds.
            </p>

            <p>
              "What a place the Otira Gorge was for a boy to get his first impression of terra firma, breathing as he did, beauty unsurpassed of its kind in the world. That beauty blending with the roll of the foaming river, to say nothing of the songs of a myriad birds at the dawning of every day. From the head of the canyon gazing westward, what pen would attempt to adequately describe those western slopes in the month of February, when the rata had flung its vesture of crimson as far as the eye can reach, with such effect that Alice in Wonderland might rub her eyes to the reality of it all, shaded in scarlet and green, and still more scarlet and green."
            </p>
          </blockquote>

          {/* Image 186B */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186B Coach travel through the Otira.jpg"
              alt="Coach travel through the Otira"
              caption="Coach travel through the Otira"
              filenameHint="186B Coach travel through the Otira.jpg"
            />
          </figure>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              The move to Motunau and "Skylight"
            </h3>
            <p>
              In 1878 Robert and Sarah, with six children, moved to Cabbage Tree Flat where they bought blocks of land (59 acres) "that ran over the hills from Skylight Gully to join the Scargill - Motunau Road...a small length of the boundary from the house along the Motunau facehold was a gorse fence."
            </p>
            <p>
              There were few fences to keep stock in - but children who could spend the day looking after them. They built a home of sod and saplings, using toi toi for the thatched roof. The original dwelling burnt down. Their first crops gave light yields but they were a start. Robert worked off farm from 1879 1886 for the Waipara Road Board and again intermittently.
            </p>
            <p>
              Granddaughter Beryl Sloss told this story.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-9">9</a></sup>
            </p>

            <blockquote className="bg-[#f3ede0] p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base italic text-[#2c221e]">
              "One time when Gran came to me I said, "Gran, how on earth did you get on...all those children?" "Oh," she said "we weren't a bit fussy about cleanliness like you are now. We lived in a sod hut with a mud floor and one room had a big bed in it and it had the boys. The other room had another bed in it and it had the girls. If the boys didn't behave, grandfather went in and just went whomp across the bed with the stick! And if the girls didn't behave they got whomp across the bed with the stick........It was really quite simple. The hardest thing I ever remember doing was carrying the water from the creek to do the washing."
            </blockquote>

            <p>
              They sold out to William Acton-Adams of Tipapa for 300 Pounds in 1890 and bought land west of Scargill, Plas Isa, but they did not move there immediately.
            </p>
          </div>

          {/* Image 186C */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186C Golden Wedding 1916.jpg"
              alt="Golden Wedding 1916"
              caption="Golden Wedding 1916"
              filenameHint="186C Golden Wedding 1916.jpg"
            />
          </figure>
        </section>

        {/* Section 4: GRANNY MARGARET BUNTING */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            GRANNY MARGARET BUNTING
          </h2>

          {/* Image 186D */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186D Margaraet Bunting 1822-1902.jpg"
              alt="Margaret Bunting 1822-1902"
              caption="Margaret Bunting 1822-1902"
              filenameHint="186D Margaraet Bunting 1822-1902.jpg"
            />
          </figure>

          <p>
            Sarah's mother and stepfather, Margaret Anne and Allan Bunting, lived in the Sefton area where Margaret was a midwife. Their headstone is at the peaceful Balcairn Cemetery, entered under a lynch gate.
          </p>

          <div className="space-y-2">
            <p className="font-serif font-bold text-[#2c221e]">
              The Bunting half siblings to Sarah Roberts:
            </p>
            <ul className="space-y-2 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-4 text-[#2c221e]">
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>Thomas</strong> (1854-1881) married Eliza Jane</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>Jane</strong> (born 1856) married M. Heinisch</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>John</strong> (born 1858)</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>Margaret</strong> (1859-1932) married Frederick James Hayman, a school teacher</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>Mary</strong> (born 1862) married S Dalzell</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="text-[#7a5b3a] font-bold text-lg leading-none mt-1">•</span>
                <span><strong>Fanniah</strong> (1863-1921), known as Fanny, married James George Pain</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Section 5: MOTUNAU MEMORIES & HOW ROBERT AND SARAH MET */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            TAKEN FROM MOTUNAU MEMORIES<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-10">10</a></sup> based on T.E. L. Roberts' story telling of how Robert and Sarah met.
          </h2>

          <p>
            From about 1864, there was a general flow of gold diggers trekking through the Waipara district, on their way to the West Coast via the Hurunui Gorge or going through to Marlborough on the coastal route. Robert would have been 22 at this time.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "At that time there was in Kaiapoi one William Alexander, a businessman of progressive disposition, and he, realising there was an opening for something of the nature of accommodation at Waipara, decided to open up. He had in his employ, or at his hand, a young sea man who had just finished his apprenticeship at sea and had come to NZ in a ship of the name of Brisk. This young man's name was Robert Morris Roberts (Bob), and he undertook the task of becoming mine host, of the first Waipara Accommodation House, or shall we say hotel," wrote T.E.L. "The equipment was of a most primitive order, being a spade on which to cook scones, a gridiron to grill chops and last, but not least, a corkscrew. Ofcourse there would be the black billy and tea and sugar bags, pepper and salt."
            </p>

            <p>
              The spot became quite a halting place and all was going along quite merrily with Bob being quite equal to the task, but it was not long before they ran up against the then-developing liquor regulations. By some miscalculation William Alexander had failed to take on some legal technical technicality, which meant he should have been paying fees.
            </p>

            <p>
              An explanation was demanded so R.M.R. tramped off to Saltwater Creek. He lodged for the night at a hotel some distance on the Leithfield side of the Creek which was not then bridged and at dawn he was ferried over the stream. When he arrived in Kaiapoi it was to discover the catering business had gone into liquidation.
            </p>

            <p>
              Or, to put it more bluntly, they were moonlighting and got caught - and young Robert quickly got out of Canterbury or a while!
            </p>

            <p>
              "In that same year, 1864, a young girl of the name of Sarah Elizabeth Baxter, also arrived in Christchurch, and just prior to R.M.R. going to Waipara, this girlie took a post as nurse with Mrs W Alexander, of Kaiapoi, and it so happened that when the Alexanders called at Philpott's Corner, now T Armstrong & Co, to pick up the new nurse girl, they had on the back seat of a dog cart the young man, R.M.R.. This little girl had not long been from her fond mother and was very near the line of tears when the cart moved off. The young man did what he could to brighten the trip to Kaiapoi, and that trip was the commencement of a very long and arduous journey, but the girlie was the equal to it, and in her 96th year crossed the Vale<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-11">11</a></sup> still mentally alert as a girl."
            </p>
          </blockquote>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              WHAT HAPPENED NEXT
            </h3>
            <p>
              Sarah settled in as nurse maid with the Alexander family and was soon to be confronted with a culinary challenge, she was given a bowl of whitebait to prepare for the dining table. She had never seen such tiny fish, and not knowing any different, she got scissors and started to try and gut them. Finding it very difficult she went to Mrs Alexander to ask if there was another way to gut them. She then found she was the centre of a big joke, one that she was never allowed to forget!
            </p>

            <p>
              Before the end of 1865 Sarah had moved with the Alexander family to the Taramakau Valley, in a wagon which was one of the first wheeled traffic over the newly opened Arthur's Pass. Regular coach services between Christchurch and Hokitika began in March 1866.
            </p>

            <p>
              At the start of 1866 Bob Morris returned to Kaiapoi. Mr Alexander was building two accommodation houses, the Junction and Midway, in Otira Gorge on the coach road to Hokitika where prospectors were travelling with hope and shovels. Sarah and Bob went with Mr Alexander to these businesses - "and from that time on their pathways lay parallel, in that at Yuletide in 1866 they were married in St Peter's Church, Upper Riccarton."
            </p>
          </div>

          {/* Image 186E */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186E St Peter_s Church, Upper Riccarton.jpg"
              alt="St Peter's Church, Upper Riccarton"
              caption="St Peter's Church, Upper Riccarton"
              filenameHint="186E St Peter_s Church, Upper Riccarton.jpg"
            />
          </figure>

          {/* Image 186F */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186F Sarah Elizabeth Roberts (1849 - 1944).jpg"
              alt="Sarah Elizabeth Roberts (1849-1944)"
              caption="Sarah Elizabeth Roberts (1849-1944)"
              filenameHint="186F Sarah Elizabeth Roberts (1849 - 1944).jpg"
            />
          </figure>

          {/* Image 186G */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186G Robert Morr5is Roberts aged 23 (1842 - 1921).jpg"
              alt="Robert Morris Roberts aged 23 (1842-1921)"
              caption="Robert Morris Roberts (1842-1921), aged 23"
              filenameHint="186G Robert Morr5is Roberts aged 23 (1842 - 1921).jpg"
            />
          </figure>

          <p>
            There were 11 accommodation houses on the route between Christchurch and Hokitika and the trip took about 36 hours. "Passengers spent the night at Bealey Corner where, after having walked up Porter's Pas and been jolted through the Waimakariri country, they prepared for an assault on Arthur's Pass. From the summit the coach road ...for 1800 feet zig zags down with such sharp turns that at several corners the leader' feet are within a yard of the edge...the brakes grind and squeak..and after two miles of cavernous, rugged rock cutting, just above the roaring blue glacier torrent, every now and then besprinkled with the spray of a waterfall, we pull up at a little shanty for a welcome lunch. Down steps an elderly gentleman from behind me, shales himself and says, "Well, sir, I'm thankful we're here, nothing shall ever induce me to come down that pass again..."<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-12">12</a></sup>
          </p>

          {/* Image 186H */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186H The Zig Zag, an  image from A Place in History Recorded.jpg"
              alt="The Zig Zag"
              caption="The Zig Zag, an image from A Place in History Recorded"
              filenameHint="186H The Zig Zag, an  image from A Place in History Recorded.jpg"
            />
          </figure>
        </section>

        {/* Section 6: SOVEREIGNS IN A STRING BAG */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            SOVEREIGNS IN A STRING BAG FROM IRELAND
          </h2>
          <h3 className="font-serif text-lg font-bold text-[#7a5b3a]">
            ENABLED THE PURCHASE OF THEIR FIRST LAND AT CABBAGE TREE FLAT
          </h3>

          {/* Image 186I */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186I The sreong bag that carried the sovereigns from Ireland to Sarah in NZ.jpg"
              alt="The string bag that carried the sovereigns from Ireland to Sarah in NZ"
              caption="The string bag that carried the sovereigns from Ireland to Sarah in NZ"
              filenameHint="186I The sreong bag that carried the sovereigns from Ireland to Sarah in NZ.jpg"
            />
          </figure>

          <p>
            Sarah Elizabeth Baxter was born in Magheralin in County Down, Ireland to James Baxter and Margaret<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-13">13</a></sup>, daughter of weaver John Reilly from the Parish of Tullyish. She was their only child as James died in 1852 when Sarah was just three years old. He did not leave a Will so his estate was left in trust for his daughter.
          </p>

          <p>
            The Widow Baxter later married Allan Bunting and they had six children. As Sarah grew up she was not well and by 1864 the local doctor was urging her mother to take her to a kinder climate as he considered the child was not likely to survive in Ireland. The whole family came to NZ on British Empire in 1864.
          </p>

          <p>
            It was when Sarah had reached her majority that efforts were made to claim her inheritance -a string bag of sovereigns.
          </p>

          <p>
            The sovereigns were sent out to Sarah in the string bag and with them she purchased the smallholdings at Motunau in August 1878, which were registered in her name. When they were later sold it enabled the purchase of the 150- ha property near Scargill, called Plas Isa. The two blocks at Cabbage Tree Flat, about 28 ha total, were in what is now the Greta Valley village, and a smaller block on the south side of the road from Motunau to Scargill.
          </p>

          <p>
            It wasn't their original intention to settle there. They had been planning to move to the North Island when a land agent Stephen Harper, an acquaintance from Robert's West Coast days, persuaded them to stay and he sold them the land. They named it Skylight and moved into the simple sod cottage with a thatched toi toi roof that Robert and another local settler had built.
          </p>

          <p>
            Most of it wasn't fenced apart from a boundary fence of black birch posts and wire with barbed wire on the top, and a gorse hedge on places. The first crops were wheat, then later oats were planted. The sheaves were tied together with plaited flax.
          </p>

          <p>
            Jim Gibb<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-14">14</a></sup> ploughed much of the land in the spring of 1889. His future brother-in-law Edward Roberts remembered this in his personal memoir.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e]">
            "When Jim Gibb was ploughing the land for the crop one of my sisters and I were supposed to be minding our cows on the road. We allowed them to graze along Tobin's road frontage, and as they were reaching through his road fence to eat his autumn-sown pats he came along on horseback and drove them to his dwelling near Scargill. Jim Gibb told my sister Margaret and me to go after him and if he had them in his yard, before taking them to the public pound at Waikari, to ask him to let us have the cows and we would not let them interfere with his crop again. When we reached Tobin's home we saw the cows were in his yard and on knocking on his door he came out. He had been having a shave before setting off to the pound with the cows. My sister told him if he would let us take the cows home they would not annoy him again, and he let us take them."
          </blockquote>

          <p>
            In August 1890 Sarah and Robert bought the 150ha in the Waikari Valley although they did not move there for about six years. Sarah's legacy of 170 Pounds enabled the purchase, and they took a 100 Pound mortgage, at 10% interest, to buy cows and farm necessities. The fencing wasn't completed for seven years.
          </p>

          {/* Image 186J */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/186J The first house at Plas Isa 1898 Sarah with granddaughters Helen and Annnie Gibb.jpg"
              alt="The first house at Plas Isa 1898"
              caption="The first house at Plas Isa 1898 Sarah with granddaughters Helen and Annie Gibb"
              filenameHint="186J The first house at Plas Isa 1898 Sarah with granddaughters Helen and Annnie Gibb.jpg"
            />
          </figure>

          <p>
            The move to Skylight brought them into close contact with the widow in the Accommodation House on Cabbage Tree Flat, Helen Gibb. Their eldest daughter Lizzie would marry Helen's eldest son, Jim. Their second daughter Jane would marry Helen's second son Stewart. The Irish and Welsh united with the Scots and a family tree began its many branches.
          </p>
        </section>

        {/* Section 7: LETTER ROBERT MORRIS ROBERTS WROTE IN 1907 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Mail className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              LETTER ROBERT MORRIS ROBERTS WROTE TO HIS CHILDREN IN 1907
            </h2>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p className="font-bold text-right text-sm text-[#7a5b3a]">
              Scargill, Nov 18th. 1907
            </p>

            <p className="font-bold">
              My dear Sons and Daughters
            </p>

            <p>
              As it's been a howling nor' wester all the morning, and has turned to a southerly this afternoon, I'm not feeling inclined to work, and having had a notion this long time that I ought to drop you a few lines for old acquaintance sakes, so here goes.
            </p>

            <p>
              You can divide the contents of this between you, as I don't suppose I shall write to you again this year and hope to have the pleasure of seeing some, if not the whole of you, for Xmas or as soon after as possible. Well, I'm going to try and give you some news such as it is, but I would not be surprised some of it will be stale.
            </p>

            <p>
              Mother and me had about 3 days at the Agricultural Show, and we enjoyed ourselves. You see in my old age<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-15">15</a></sup>, I have to take mother to take care of one, or else I am very apt to leave my new coat or brella in some stray Tea Shop, but mother never leaves anything behind her, but if she does she lets it stop there, I don't. I go back quietly.
            </p>

            <p>
              We did not stop at Sefton either going or returning as Influenza<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-16">16</a></sup> was prevalent, but we put a night in at Kaiapoi, but shan't trouble to do so again, not for a long time. The show was a failure from my point of view - as there were no implements for the grounds, and that is about the only thing I am interested in as far as the show is concerned.
            </p>

            <p>
              There was a record crowd there, about 32,000. A good few more than last year, though they had the Exhibition<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-17">17</a></sup> crowd to draw from. We got a bed at Holmes<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-18">18</a></sup> and some first-class tucker, and we both got home clean and decent, as honest people should.
            </p>

            <p>
              Tom was at home during my lambing and like a good fellow took the responsibility of the lambing on himself, which saved my old legs a lot of superfluous exercise for which I was thankful. Well, he went down south about 3 weeks ago and I heard from him a few days ago saying he found the boys<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-19">19</a></sup> and everything first, class crops and stock included - he is to be home<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-20">20</a></sup> for Xmas. Sam<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-21">21</a></sup> went to the North Island, but I don't hear much about his movements. Mary writes pretty often, and by her letters is well pleased of things, at least I hope so. We are going to try to get down to see them after harvest.
            </p>

            <p>
              Talking of harvest, my crops look well especially the autumn sown oats, grass plentiful, sheep satisfied. Old cow gave us a heifer calf, and I expect Poly the mare to give us a foal on a day or two. We had 100% of lambs, 3 or 4 died after tailing and a few born since we tailed. 8 this morning from the paddock, the nor' wester was so severe we did not trouble the others. My onions and parsnips are a failure. I could not get them in in time as the ground was so wet, but the potatoes, what few we put in are looking well, and if the frosts keep away we will have a record harvest of apples.
            </p>

            <p>
              I have finished my stable and gave it 3 coats of paint the same colour as the house, and I have just completed building a wheelbarrow<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-22">22</a></sup> so that Bert will be set up again once more, he missed the old barrow awful, but he broke it himself. We had Mrs Louise Barber here with 2 children from Saturday till today. She was on the road home from a holiday to get her teeth fixed, which took a fortnight.
            </p>

            <p>
              Fred, Mary and Tommy took her over to Jean and Stewart's yesterday as it was a beautiful day. We are not going to get any rain I am afraid as the S Wester which came up about an hour ago has turned out a dry one so far, and we want rain badly to liven things up and keep them going.
            </p>

            <p>
              All the Gibb's are alive and most of them are well. George<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-23">23</a></sup> is still single, but I suppose he will rectify that in the near future, and if he don't he ought to. We have a new neighbour, Banks sold out of his place (Digbys) and a man named Pannett bought him out. I went over the other morning to see if he would pass and came to the conclusion that he will do. His family has not come up yet as he has some property near Ch-Ch to dispose of. Banks bought 3 or 4,000 acres about Clinton, that's between Dunedin and Invercargill. I think there is a syndicate of 3 in it. The Leaches<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-24">24</a></sup> have bought a little over 2,000 acres at Swannanoa or close to Eyreton from J.J. Peacock and they are going to sell the Ashley property. Walter, the one here, will stop where he is, the other 2 brothers with their father will live on the larger estate as it has 2 houses, one of 10 rooms, the other 7.
            </p>

            <p>
              We have a new storekeeper. I think he is a pretty hard nail as a country storekeeper has to be, but I never go near him. I have not been as far as the store for more than 3 months. The shearing has started at the Scargill shed. They have a new manager for the board<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-25">25</a></sup> as Angus Cameron was full up of it. You will see 3 large new houses near us when you come down. It's a tossup which is the best, it lays between McLellands and Harrisons.
            </p>

            <p>
              Mrs McLelland and Rubie came to grief at the railway station just before the races. They got too near the train with a young black horse and gig. The horse took fright - Mrs Mac collared the steering gear and started to yell at 40 horse-power to encourage the horse to do his best, which he endeavoured to do to the best of his ability for he took the gig with contents and negotiated a number 8 wire fence at the top end of the station yard. He managed without much difficulty to get over the fence himself as he was a trained jumper, but he could not get the gig over, but I dare say he might be able to do so in the future after a little practice.
            </p>

            <p>
              But anyway poor Mrs Mac did not go to the races this year or Mac either. They got off very lightly considering. Got a few bruises over the faces but no broken bones. Mrs Daly was at Holmes' - she always enquires after you. Her husband is working on the railway near you, she told me she is going up to join him shortly. Now as its near tea-time, and I think my pen is getting dry, as it's a fountain one, or I should not write so much, I will draw to a close and hope that these few lines will find you all well.
            </p>

            <p>
              I should dearly like to see you all down at Xmas but I suppose that's out of the question, though there is plenty of floor space in the stable loft - it will hold very near 10 tonnes of chaff so there is plenty of room. To any of you who can't be here for Xmas we wish you a very happy one and a prosperous new year and that you may all enjoy good health, which is better than all the prosperity on the earth. So mother and me unite in kind love to you all,
            </p>

            <p className="pt-2 font-bold">
              From your affectionate Father and Mother<br />
              R & S Roberts
            </p>

            <p className="text-sm italic pt-2 border-t border-[#d8ceb9]">
              Ted and Jennie<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-26">26</a></sup> have a half share in this epistle, and don't forget - so pass it on.
            </p>
          </blockquote>

          <p className="font-serif italic text-sm text-[#5c4328]">
            There was an old shearing song with the line..."There on the board, the old shearer stands, holding his shears in his old gnarly hands...".
          </p>
        </section>

        {/* Section 8: COMPARATIVE TIMELINE TABLE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            Comparative Timeline: Robert & Sarah Roberts
          </h2>

          <div className="overflow-x-auto rounded-2xl border-2 border-[#d8ceb9] bg-[#fbf9f4] shadow-xs">
            <table className="w-full text-left text-sm border-collapse font-serif">
              <thead>
                <tr className="bg-[#f3ede0] border-b-2 border-[#d8ceb9] text-[#2c221e]">
                  <th className="py-3 px-4 font-bold text-xs uppercase tracking-wider w-24 border-r border-[#d8ceb9]">Year</th>
                  <th className="py-3 px-4 font-bold text-xs uppercase tracking-wider border-r border-[#d8ceb9]">Robert</th>
                  <th className="py-3 px-4 font-bold text-xs uppercase tracking-wider">Sarah</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8dec9] text-[#2c221e] font-sans text-xs sm:text-sm">
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1842</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Born 5 October, at Daventry</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1844-49</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Lived with Welsh grandparents at Penmaenmawr</td>
                  <td className="py-2.5 px-4 align-top">Born 23 December 1849 at Lismaine, Magheralin</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1852</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Her father James Baxter died. Mother Margaret married Allen Bunting 26 June</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1854</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Apprenticed as a cabinetmaker</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1858</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Apprenticed to sea until March 1862</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1862</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Arrived in NZ</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1864</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">On drain construction near Kaiapoi then went to Otago goldfields and back for some moonlighting</td>
                  <td className="py-2.5 px-4 align-top">Arrived in NZ, took position at nurse maid with Mrs Alexander at Kaiapoi</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1865</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Goldfields, visited his brother Thomas in Queensland</td>
                  <td className="py-2.5 px-4 align-top">Went to Taramakau with the Alexander family</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1866</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Working with Mr Alexander at Otira</td>
                  <td className="py-2.5 px-4 align-top">Married 24 December at Riccarton</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1867-1875</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">
                    Taramakau. In 1870 they sold Taramakau Junction House to Michael Jackson. 1872-74 they leased the Otira Gorge Hotel at the base of the Zig Zag. He often drove stock to Canterbury, delivered mail around Lake Brunner, did maintenance on the Otira side of the Pass. The family stayed at Sefton with the Buntings, then rented a cottage at Ashley while he worked on railway construction, then lived at Leithfield and Balcairn. Later worked for 3 months for Alexander as a travelling draper with horse and van, then 3 months on Mt Grey Station
                  </td>
                  <td className="py-2.5 px-4 align-top">
                    Eliza Catherine born 1869. Jane born 1871. T.E.L. born 1873. Robert Morris born 1874. She maintained the House, fed the travelling public, served them in the bar - when a patron wanted to buy her a drink she filled her glass with cold tea from a bottle she kept under the counter (acting on her husband's advice).
                  </td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1876</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">James Baxter born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1878</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Decided to move to North Island but persuaded to buy land at Greta Valley, moved to a sod cottage on Skylight, and got work for the Waipara Road Board until 1886</td>
                  <td className="py-2.5 px-4 align-top">Margaret born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1881</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Edward born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1882</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Mary born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1884</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Fanny born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1886</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Off work for 3 months when an old ulcer on his leg opened up. It was a severe winter.</td>
                  <td className="py-2.5 px-4 align-top">George Lindsay born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1887</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Herbert John born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1889</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Worked on Whale's Back Road, arched stone culverts, Hurunui - Cheviot Road.</td>
                  <td className="py-2.5 px-4 align-top">Frederick born</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1890</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">
                    Sold Skylight, bought Plas Isa near Scargill, which was leased out as they moved to Happy Valley rent free in exchange for clearing 70 acres of gorse. Worked also on Stoneyhurst and Cheviot Hills
                  </td>
                  <td className="py-2.5 px-4 align-top">1891-Eliza married Jim Gibb. 1893-Jane married Stewart Gibb</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1896</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Moved to Plas Isa</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1908</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">Retired to St Albans</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1914</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">George killed in dray accident. Eliza died of heart failure</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1921</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top">20 March, died in Christchurch, buried at Bromley Cemetery</td>
                  <td className="py-2.5 px-4 align-top"></td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1932</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">Herbert died of cancer</td>
                </tr>
                <tr className="hover:bg-[#f3ede0]/50">
                  <td className="py-2.5 px-4 font-bold font-serif border-r border-[#d8ceb9] align-top bg-[#f3ede0]/30">1944</td>
                  <td className="py-2.5 px-4 border-r border-[#d8ceb9] align-top"></td>
                  <td className="py-2.5 px-4 align-top">7 September, died in Christchurch, buried at Bromley with Robert</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 9: THE CHILDREN OF ROBERT AND SARAH ROBERTS */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            THE CHILDREN OF ROBERT AND SARAH ROBERTS
          </h2>
          <p className="font-medium text-[#4a3525]">
            Robert Morris and Sarah Elizabeth had 12 children in 18 years and all survived childhood which was quite an achievement in that time.
          </p>

          <div className="space-y-8">
            {/* Lizzie */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Lizzie</strong>, Eliza Catherine (1869-1914) married James Lindsay Gibb of Heathfield and they had Helen Isabel Cole, Margaret Annie Denton, Robert Morris Roberts and Catherine Lindsay (Lina). She was petite, with dark brown hair and a strong brow line. She walked with a limp, perhaps resulting from the carrying of a young sibling for much of her childhood, although her siblings had been affected by childhood polio. Lizzie was the mainstay in the home to help her mother, and to mother the younger children - and she was quite capable of giving them a good smack and a lecture when it was necessary. She suffered ill health before her early death.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/187 Eliza Catherine Roberts, known as Lizzie.jpg"
                  alt="Eliza Catherine Roberts, known as Lizzie"
                  caption="Lizzie"
                  filenameHint="187 Eliza Catherine Roberts, known as Lizzie.jpg"
                />
              </figure>
            </div>

            {/* Jennie */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Jennie</strong>, Jane (1871-1954) married Stewart Burnett Gibb Jnr and they had Bernard Stewart (Burnie), Mildred Clara Tarbotton, Francis Helen Lennie, Annie Margaret, Edward Lindsay, Olive, Greta Aroa, George, Winifred Alice Brown, Edward and Olive. Jane (Jennie) was a partial cripple through polio, and she walked with a limp. She loved her garden and had green fingers. She had a buoyant nature, could quote verses to suit an event, and she loved and excelled at making jams, jellies, breads. Her family remembered her as a lovely, loved and loving person who enjoyed a wonderful companionship with her husband. Jennie left home at the age of 11 to be company for Annie Arkle, who was housekeeper for her grandfather, a Happy Valley cattle farmer. She was there for 7 years before working for the wife of the first manager of Tipapa<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-27">27</a></sup>, which included cooking for the four permanent station hands, including Stewart Gibb. They celebrated their golden wedding anniversary.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/188 Jane Roberts, known as Jennie.jpg"
                  alt="Jane Roberts, known as Jennie"
                  caption="Jennie"
                  filenameHint="188 Jane Roberts, known as Jennie.jpg"
                />
              </figure>
            </div>

            {/* Tom */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Tom</strong>, Thomas Edward Lloyd (1873-1952), known as Tom, married Chrystabel Murray. He farmed on a block from the Starborough Estate near Tetley Brook inland from Ward, called Brooklyn, then took over Plas Isa from his parents, in the Waikari Valley. They had two surviving daughters, Leila Margaret Grubb and Beryl Mea Sloss. He had a great vocabulary, was a genius at painting word pictures, always had a jingle, and covered his account book with lines of poetry. He played the violin. His legacy of stories is admirable and highly valued.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/189 Thomas Edward Lloyd Roberts, known as Tom.jpg"
                  alt="Thomas Edward Lloyd Roberts, known as Tom"
                  caption="Tom"
                  filenameHint="189 Thomas Edward Lloyd Roberts, known as Tom.jpg"
                />
              </figure>
            </div>

            {/* Morris */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Morris</strong>, Robert Morris Jnr (1874-1960) married Phoebe Armstrong, and after many years of a variety of farm work he farmed first at Blind River near Lake Grassmere and then he bought Troquear on Needle Point Road near Blenheim, and it remains in family ownership. Their children were Arthur Gordon Roberts, Ethel Margaret McNabb, Greta Constance Kennington, Frank Morris Roberts and Marjorie Edna McNabb. He was very musical and could play a violin, accordion, mouth organ, tin whistle all by ear. He was very keen on poetry and could recite long narrative poems such as The Man from Snowy River. He was a great storyteller and a good mimic.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/190 Robert Morris Roberts jn, known as Morris or Murray.jpg"
                  alt="Robert Morris Roberts jn, known as Morris or Murray"
                  caption="Morris"
                  filenameHint="190 Robert Morris Roberts jn, known as Morris or Murray.jpg"
                />
              </figure>
            </div>

            {/* Baxter */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Baxter</strong>, James Baxter (1876-1953) married Eleanor Gertrude Bateman. They farmed at Orari near Geraldine in South Canterbury and had four children: Eleanor Meta Allen, Beth (Elizabeth) Sarah Kingdon, Louisa Mary Joyce Mallard and James Baxter Roberts who inherited the farm, which is still in family ownership at 2021. He too loved to tell stories, especially about their early years. When the brothers got together they would all tell stories for hours, and many of those yarns had a comical twist at the end.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/191 James Baxter Roberts, known as Baxter.jpg"
                  alt="James Baxter Roberts, known as Baxter"
                  caption="Baxter"
                  filenameHint="191 James Baxter Roberts, known as Baxter.jpg"
                />
              </figure>
            </div>

            {/* Maggie */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Maggie</strong>, Margaret, (born 1878), looked after everyone including 15 years with her brother Edward after he was widowed with a baby son. She looked after her mother for the last 16 years of her life. Her brothers wrote of her huge heart.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/192 Margaret, known affectionately as Maggie.jpg"
                  alt="Margaret, known affectionately as Maggie"
                  caption="Maggie"
                  filenameHint="192 Margaret, known affectionately as Maggie.jpg"
                />
              </figure>
            </div>

            {/* Ted */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Ted</strong>, Edward (1881-1975), married Sarah Jane Trueman who died after childbirth, leaving a son, Robert Charles Edward Trueman Roberts. Edward later married Eva Fanny Rinaldi, and they had three daughters: Elizabeth Margaret Fordyce, Helen Evangeline Ford, Ailsa Catherine Fraser. He drew a farm he named Riverside, in the June 1908 ballot of the Cracroft-Wilson Culverden Estate, and this is still in family ownership. He was a literate, well-read man, a keen correspondent, amateur musician, fond of recitation, a hard-working farmer, churchman, involved with the Farmers Union and generous to those in need. He wrote "A Personal History" that is invaluable for his family's history.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/193 Edward Roberts, known as Ted.jpg"
                  alt="Edward Roberts, known as Ted"
                  caption="Ted"
                  filenameHint="193 Edward Roberts, known as Ted.jpg"
                />
              </figure>
            </div>

            {/* Mary */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Mary</strong> (1882-1938) married Robert Alexander Craighead. Their children were Francis Eileen, Harriet Isabella, Anne Gavin Cations, Robert, Samuel, Douglas Bruce and Cleave. Her brother Ted called her a great talker.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/194 Mary Roberts.jpg"
                  alt="Mary Roberts"
                  caption="Mary"
                  filenameHint="194 Mary Roberts.jpg"
                />
              </figure>
            </div>

            {/* Fanny */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Fanny</strong> (1884-1963) married Samuel Craighead. Their children were Samuel Lindsay Alexander, Lloyd, Mary Mavourneen (called Monnie) Connor and Murray Roberts Craighead. She was a member of the Red Cross, a keen gardener, with a great faith in herbal brews, and she was a strict vegetarian.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/195 Fanny Roberts.jpg"
                  alt="Fanny Roberts"
                  caption="Fanny"
                  filenameHint="195 Fanny Roberts.jpg"
                />
              </figure>
            </div>

            {/* George Lindsay */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>George Lindsay</strong> (1886-1914) did not marry, worked in many areas, farmed at Scargill, and was killed in an accident with a dray on his 28th birthday, 14 February 1914.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/196 Geoirge Lindsay Roberts.jpg"
                  alt="George Lindsay Roberts"
                  caption="George"
                  filenameHint="196 Geoirge Lindsay Roberts.jpg"
                />
              </figure>
            </div>

            {/* Bert */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Bert</strong>, Herbert John (1887-1932) was born with a club foot, and may have had a deformed leg caused by polio. He never married. He was the orchardist and gardener at Riverside and helped on the farm. He lived in a hut with a bed, and open fire, in the orchard, and had his meals in the main house. He was a smoker and died of lung cancer.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/197 Herbert John Roberts, known as Bert.jpg"
                  alt="Herbert John Roberts, known as Bert"
                  caption="Herbert / Bert"
                  filenameHint="197 Herbert John Roberts, known as Bert.jpg"
                />
              </figure>
            </div>

            {/* Fred */}
            <div className="space-y-4 bg-[#f3ede0] p-6 rounded-2xl border border-[#d8ceb9]">
              <p>
                • <strong>Fred</strong>, Frederick (1889-1970) married Lyla (Eliza) Whyte, daughter of Elizabeth and Arthur Wilde Esq, of "Rose Cottage" at Waitahuna, Southland on 30 September 1914. They had six children, and took over Plas Isa when his brother retired, winning accolades from the family for the high state of production he achieved.
              </p>
              <figure>
                <ArchivalImageCard
                  src="public/images/198 Frederick Roberts.jpg"
                  alt="Frederick Roberts"
                  caption="Fred"
                  filenameHint="198 Frederick Roberts.jpg"
                />
              </figure>
            </div>
          </div>
        </section>

        {/* Section 10: GEORGE LINDSAY ROBERTS LETTER */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Mail className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              GEORGE LINDSAY ROBERTS WROTE TO HIS SISTER ABOUT.....wishing he had a girl, when he was 25 and living in Central Otago
            </h2>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p className="font-bold text-right text-sm text-[#7a5b3a]">
              Waitahuna<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-28">28</a></sup><br />
              Aug 28th 1911
            </p>

            <p className="font-bold">
              Dear Maggie
            </p>

            <p>
              This is my washing night so while the sunlight soap is doing its work I will write to you, not that I have anything to tell you, still it will be pretty hard If I can't write you a letter without having something to write about.
            </p>

            <p>
              Well, the weather is generally a sure topic to start on, it has been splendid, today was a real spring day, but there it might break any day. I am having a dreadful time, last Monday morning when I went up to the "Enfield" homestead, first thing Baxter introduced was a birthday cake, that wasn't so bad, but when Fred came along and produced a silk handkerchief- jealousy seemed to lay hold of me and I could not help thinking, why haven't I got a girl, and since then the thought has been constantly there.....why not have half a dozen girls, then you can have a choice when you have made their affection.
            </p>

            <p>
              Had a letter from Mary<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-29">29</a></sup> the other day, they are all in good form, was looking for news. Fred has finished his ploughing, and we are going to start poisoning soon, there is a lot of rabbits here as we have had no time to kill them yet.
            </p>

            <p>
              We are going to kill some of the pigs as soon as we get time, but we have been talking like that for a good while now, there is about twenty more little ones. I don't know how many there is now, but they nearly drive me to drink. I think it is impossible to make a fence to stop pigs. I have all my fencing material home now, got the posts today, so will be able to get the garden fenced, and the trees planted.
            </p>

            <p>
              Well Maggie I have finished my washing and it is bedtime, so good night. Remember me Tom and Chrys<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-30">30</a></sup>.
            </p>

            <p className="pt-2 font-bold">
              I am your loving brother<br />
              George<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-31">31</a></sup>
            </p>

            <p className="text-sm italic pt-2 border-t border-[#d8ceb9]">
              P.S. Tell Bert<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-32">32</a></sup> if you ever see him that I have been looking for a letter from him for a long time.
            </p>
          </blockquote>

          <p>
            George worked in North Canterbury, spent most of 1904 at Starborough in Marlborough, had 1905 with his brother Edward, fencing at Morton Mains, then went later to the Hakataramea Valley as a rabbiter on the farm adjoining land that his brothers Tom and Baxter had taken up. Tom went to the UK in the autumn of 1906, and on his return, towards the end of 1906 Tom bought land at Waitahuna, joined later by Baxter who sold his property to join his brother and bought about 280 ha adjoining. It was not a successful venture. Tom sold up and bought the Taramea block at Scargill, from Acton-Adams, about 1915. Baxter was back in the Hurunui by 1916 managing the Stewart Estate. He would end up at Orari in South Canterbury.
          </p>
        </section>

        {/* Section 11: LETTER JIM HAYMAN TO BERT ROBERTS */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Mail className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              LETTER JIM HAYMAN (married to Margaret, half-sister to Sarah Elizabeth Roberts) wrote to Bert Roberts, who was living at Riverside, Culverden
            </h2>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p className="font-bold text-right text-sm text-[#7a5b3a]">
              Oruanui<br />
              Via Rotorua<br />
              8-8-21
            </p>

            <p className="font-bold">
              Dear Bert
            </p>

            <p>
              How glad were we to get your lovely letter, to hear that you were all so well and that you were spending your time in such useful work.
            </p>

            <p>
              Yes, I retired from school work end of July and the Education Department has got me back for a while to open a new Māori School at Otukou on the Great Tongariro National Park. This I have promised to do for a little while till they can get a teacher.
            </p>

            <p>
              Yes Bert, your offer to help in the Maori Mission work has come just in a very good time. We want to now raise money enough to get an Organ for our Māori Sunday School at Oruanui. Could you help us with a few pounds it would be just great.
            </p>

            <p>
              We are all well, that is your Auntie, Gordon McBeath-Hayman our little "Son-Grandson" who is now 10½ years and is in the 5th standard, and your old Uncle Jim who does not feel so old after all.
            </p>

            <p>
              We have a nice little comfortable home at Oruanui where we are now for some time anyway. It is on 52 acres of Government Perpetual 66-year lease land.
            </p>

            <p>
              Mabel, that is Mrs Sydney Palmer and her husband and family of 3 live at Oruanui. They own the Totara Sawmills there. Charlie is at Tauranga on the Bay of Plenty, doing well on a little farm. Walter is Manager of the Sawmills of Ellis & Burnaud at Manunui in the King Country.
            </p>

            <p>
              Fred is at Henderson, north of Auckland on a very large Orchard Farm. Bessie is now Mrs Tom Ross, Captain of a Steamer running from Helensville to Dargaville on the Kaipara Harbour, and Roly is one of the chief clerks in the Lands and Deeds offices at Gisborne. He is married and they have a lovely home there.
            </p>

            <p>
              Te Heuheu<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-33">33</a></sup>, the Greatest Maori Chief of the North Island, has recently died and was buried at Waihi on Lake Taupo, where there was the biggest Tangi known for many years took place.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-34">34</a></sup> <sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-35">35</a></sup>
            </p>

            <p>
              The war is over only in part so it seems a long time in stopping. God bless you Bert in your body and soul and spirit as I pray thus for all your friends and loved ones, to whom you will please give our ever constant though silent love.
            </p>

            <p>
              God is good.
            </p>

            <p className="pt-2 font-bold">
              Your old Uncle Jim.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-36">36</a></sup>
            </p>
          </blockquote>

          <p className="font-serif italic text-sm text-[#5c4328]">
            The small St Matthews Māori Mission Church at Oruanui, now a community church, was restored in 2011
          </p>
        </section>

        {/* Section 12: ACCIDENTAL DEATH OF GEORGE & UNTIMELY DEATH OF LIZZIE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            The Accidental Death of George Lindsay Roberts and the untimely death of Lizzie
          </h2>
          <h3 className="font-serif text-sm sm:text-base font-bold text-[#7a5b3a]">
            From "A Personal History" by Edward Roberts, page 104 & page 106
          </h3>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "On Sunday 15 February 1914 I took a frame of honey from one of my bee boxes to Ben Hampton and his sisters at Kairoma, and on arriving home before noon I found Stewart Gibb and my sister Jenny had just arrived and I said how glad Maggie and I were to see them. Stewart said he brought sad news. He had come to tell me that my brother George had been killed the day before on the east side of the stream between Scargill and the recreational domain. George was 28 that day and had been working ground on "Taramea" and sometime in the afternoon started back for Plas Isa with the horses in the dray Father had bought at the Cheviot clearing sale in autumn 1893. While he as shutting the gate the horses began to bolt and get the reins; but before he could get proper control of the horse the dray wheel next to the fence ran up a wire stay and the dray overturned. An iron prop for holding the frame of the dray when a frame was required caught him in the chest and pinned him to the ground. Death would have been instantaneous. My sister Margaret and I drove to Waikari with my grey horse Dougald I the gig and had dinner at Mr and Mrs Robert Darrock's home in Waikari. After the midday meal, Mr Darrock went with us to the cemetery and waited for the funeral to arrive from Plas Isa. Father and Mother were among those arriving from Scargill. My sister Lizzie<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-37">37</a></sup> did not attend as she was unwell and died 23 February from heart failure."
            </p>
          </blockquote>

          {/* Image 199 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/199 George Linday Roberts headstone at Waikari Cemetery.jpg"
              alt="George Lindsay Roberts headstone at Waikari Cemetery"
              caption="The headstone behind is for Helen Gibb, next to her granddaughter, Lina Gibb."
              filenameHint="199 George Linday Roberts headstone at Waikari Cemetery.jpg"
            />
          </figure>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "Our sister Lizzie (Mrs James Gibb)<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-38">38</a></sup> died at her home Heathfield about two weeks after the death of our brother George. I hired a car for the afternoon from Hawke's Garage in Culverden to drive Margaret, the baby and me to the Waikari cemetery to attend the funeral. The grave was close to that of our brother George. When Jim Gibb and family arrived home after the funeral they found Mr Bill Tully there and he had milked the cow and had the fire burning. Tully was generally around to lend a hand when there was trouble."
            </p>
          </blockquote>

          <p>
            Lizzie's daughter Annie recalled that she was in the orchard when she saw her mother walked in a very frail way down the path to the outside toilet. She was annoyed that her sisters had let her leave the house when she had been so unwell. Annie saw her mother fall down and she had reached her in a wild run, fearing the worst and just one look was enough to confirm that fear. Lizzie was just 45.
          </p>

          <div className="space-y-4 pt-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              A WORD ABOUT MR TULLY
            </h3>
            <p>
              William Tully, a tall and lean man, had a property at Happy Valley (Motunau Plain) but when he took the position of married couple on Mount Vulcan for the Reece family he let the Robert family have his place rent free to clear the land, for 4 years. He had his wife Kate (nee Arkle) had two daughters, Kate and May and they had lived in a small sod cottage, with a very small kitchen with an earth floor.
            </p>
            <p>
              Helen Gibb gave Robert and Sarah two weaner pigs in 1887 which Mr Tully killed for them - a Yorkshire white dressed at 127 kg and a black Berkshire dressed at 108 kg. Edward Roberts said he never remembered anyone but Mr Tully who killed the pigs - and baked hams were a popular picnic dish and given to those setting out on overland journeys.
            </p>
            <blockquote className="bg-[#f3ede0] p-5 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base italic text-[#2c221e]">
              "It is on record that wild pigs were very plentiful on Stoneyhurst, many hundreds being taken off in some seasons. P. Goldin was one successful pig killer, as was W. Tully. The Stoneyhurst pigs had the name of being rather above the average wild type in that good sires had at times strayed from the homestead to leave an improved stamp." (T. E. L. Roberts: Motunau)
            </blockquote>
          </div>
        </section>

        {/* Section 13: THE LINK WITH COUNTY DOWN, IRELAND */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            THE LINK WITH COUNTY DOWN, IRELAND
          </h2>

          <p>
            Little is known of the Baxter family, of which Sarah Roberts was a member.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-39">39</a></sup> Her father, James Baxter, held land in Upper Canada and on his return to Ireland found that the girl who had jilted him was now a widow. He married her and nothing more is known about her except that her name was Mary, that she died and was buried at Magheralin, County Down, in 1842. There were no children from that marriage.
          </p>

          <p>
            James Baxter married secondly at the Registrar's Office in the town of Lurgan, by license, 5th June 1848. His younger bride Margaret was the daughter of John Reilly, weaver. There were four girls in the family, but during the 1845-52 Irish Potato Famine<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-40">40</a></sup> two of the girls died of typhoid fever.
          </p>

          {/* Image 200 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/200 Baxter wedding certificate notes.jpg"
              alt="Baxter wedding certificate notes"
              caption="Notes by Edward T. Roberts, Courtesy Ailsa Fraser"
              filenameHint="200 Baxter wedding certificate notes.jpg"
            />
          </figure>

          <p>
            James described himself as son of James Baxter, farmer, and that he himself was a farmer at Lismaine in the Parish of Maralin. Margaret was said to be from Clare in the Parish of Tullyish. Valentine Leathem and Esther Leathem were witnesses of the marriage. The only child of that union was Sarah Elizabeth Baxter, born and baptized in the Parish of Maralin (or Magheralin), County Down, Ireland, 23rd December 1849.
          </p>

          <p>
            James Baxter, who died from the effects of the re-opening of a wound received while he was in Canada, was buried in the Maralin Parish Cemetery on 12th February, 1852. No will was found so his estate was placed in trust for Sarah Elizabeth Baxter, entrusted to her mother, William McCullough (said to be a cousin) and Hugh Lavery. (This money eventually purchased the two Cabbage Tree Flat blocks in 1878.)
          </p>

          <p>
            James Baxter had a sister Betty, whose burial is entered in the Parish Register at Magheralin, for 10 January, 1859, aged 81 years. A brother, William Baxter was buried 19 January, 1860, aged 63 years. Another brother, John Baxter, is thought to have died before Betty and William. John had a son, James, who married Polly Costly. It is thought that James and Polly lived in Glasgow, Scotland, and that Polly came back to nurse Uncle William in his last illness. Neither Betty not William married.
          </p>

          <p>
            From Edward T. Robert's research, it appears the Baxters were not Magheralin natives but came in from the surrounding districts, and used Maralin as a burial ground.
          </p>

          <p>
            James Baxter's mother or maternal grandmother was supposed to have reached the mature age of 104 years!
          </p>
        </section>

        {/* Section 14: RETIREMENT AT 137 BEALEY STREET */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            RETIREMENT AT 137 BEALEY STREET, later called Champion Street, near Edgeware Road
          </h2>

          <p>
            It was a solid brick house with a big rear vegetable garden. Behind the house there was a sunny asphalt area with an adjoining bathroom. The pantry - kitchen had an old electric stove oven but the dishes were washed in a basin with water heated for that purpose. The brick outhouse was covered in ivy which had a flush toilet with a chain with a long chain with an ivory handle. The home at Bealey street was the centre for family life, grandchildren stayed there while studying, visitors from the country coming to town made their base there. Her husband taught her to read and write, and she enjoyed reading. She had a wonderful memory and delighted her family with tales of yesteryear. She wrote letters to her family.
          </p>

          {/* Image 201 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/201 Roberts retired to 137 Champion Street in Christchurch.jpg"
              alt="137 Champion Street"
              caption="137 Champion Street"
              filenameHint="201 Roberts retired to 137 Champion Street in Christchurch.jpg"
            />
          </figure>

          <p>
            Women got the right to vote in 1893, 10 weeks ahead of a general election. Sarah was on the Ashley Electoral Roll of 1894. She told her son that she dressed in her best, saddled a horse and rode into Waikari to cast her vote in the 1896 election.
          </p>

          {/* Image 202 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/202 Sarah Roberts wwith son Morris and granddaughter.jpg"
              alt="Sarah Roberts with son Morris and granddaughter"
              caption="Sarah with her eldest son Morris and granddaughter"
              filenameHint="202 Sarah Roberts wwith son Morris and granddaughter.jpg"
            />
          </figure>

          {/* Image 203 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/203 Sarah Roberts aged 85.jpg"
              alt="Sarah Roberts aged 85"
              caption="Sarah, in 1935 (aged 85, a little figure always dressed in black. She remembered when the foundations of Christchurch Cathedral were only 15 cm high, when it was consecrated in 1881 and completed in 1904."
              filenameHint="203 Sarah Roberts aged 85.jpg"
            />
          </figure>

          <p>
            When Sarah Elizabeth Roberts died in 1944 she had 100 living descendants. She always wore black. Her granddaughter Ailsa Fraser, daughter of Edward Roberts (known as Ted) said that whenever a young relative visited Sarah always gave them a small coin, which in those days was not an insignificant token. Her daughter Maggie (born 25 July 1878) continued that enjoyable tradition.
          </p>

          <p>
            For Richard Roberts, one of his early childhood memories was the sight of his great grandmother Sarah laid out of on her bed, surrounded by flowers the women of the family had prepared for her. She was cheerful, was hard of hearing later in life, and had the genuine love and affection of her family. Richard recorded much family history, in the tradition of his father (Edward T.) and his grandfather Edward (Ted) Roberts.
          </p>

          <div className="space-y-4 pt-2">
            <h3 className="font-serif text-lg font-bold text-[#2c221e]">
              little story from autumn 1886, when he was aged 5, from Edward Robert's "A Personal History"
            </h3>
            <blockquote className="bg-[#f3ede0] p-6 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e]">
              "Mushrooms were growing at hand and we tried eating them raw but found them unpalatable and decided the next time we took the pigs for an outing we would take matches and light a fire to cook the mushrooms. So early next morning unknown to Margaret, Mother or anyone I put a small box of matches in my pocket, in readiness to light the fire on which to cook our morning feast. But as the Bible says, nothing that is hidden shall not be revealed, or in other words all secrets shall be revealed. Margaret was putting her boots on and was short of a bootlace and thought I had it. We were arguing about it when Mother got hold of me to go through my pockets in search of the bootlace. I put up what fight a small boy can and kept repeating "no bootlace there" but my protests were in vain. Mother found no lace on me but a box of matches. For having them I received a few slaps and lecture into the bargain for carrying matches. Margaret told me when we got out of Mother's hearing she had no idea I had the matches on me to make the morning fire or she would never have complained about the bootlace which I never had taken."
            </blockquote>
          </div>

          <p>
            FIRE was a great concern. Some of the causes of accidental rural fires were (i) containers of tar being boiled over small fires to use the tar to pain fence posts and they sometimes boiled over, (ii) matches dropped from lighting pipes and (iii) brew up fires for billy tea getting away. Steam trains were notorious for causing fires along the railway lines.
          </p>

          <div className="space-y-4 pt-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              OBITUARY FOR SARAH who died 7 September 1944
            </h3>
            {/* Image 204 */}
            <figure className="my-6 sm:my-8">
              <ArchivalImageCard
                src="public/images/204 Obituary for Sarah Roberts.gif"
                alt="Obituary for Sarah Roberts"
                caption="From The Press, thanks to Richard Roberts"
                filenameHint="204 Obituary for Sarah Roberts.gif"
              />
            </figure>
          </div>
        </section>

        {/* Section 15: HAPPY MEMORIES FROM HAPPY VALLEY */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            HAPPY MEMORIES FROM HAPPY VALLEY (1890 - 96)
          </h2>

          <p>
            Tom Roberts was 17 when the unfenced Waikari Valley land, with no dwellings, was bought and decades later he wrote that he "had no hesitation in getting a camp outfit and a couple of horses and plough with the idea of setting in to plough for spring sowing of sheep feed. The camp was erected close to the Waikari stream....... That little chimney will probably still mark the spot of that first venture of the boy in all his pride of doing."
          </p>

          <p>
            He had good intentions but not the necessary skill to do so! "The plough was set in motion but expert knowledge was not quite equal to the requirements, although the work went on til Jas. Tobin passed along, and with a slight adjustment of the skeith<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p2-41">41</a></sup>, left the single furrow running like perfection. The operation was in full swing when two incidents happened which turned the tide of events for a number of years."
          </p>

          <ol className="space-y-3 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-4 text-[#2c221e] list-decimal list-inside font-serif">
            <li className="leading-relaxed">
              <strong>Robert Johnston of Waikari</strong> offered to lease the block of land - which did not end well as he put up some fencing but later fled to Australia when debts caught up with him, and he never paid any rent.
            </li>
            <li className="leading-relaxed">
              <strong>William. Tully of Happy Valley</strong> offered Robert Morris Roberts his property for a number of years free for the clearing of gorse and scrub. That had much appeal as there was a house on the property and there were 12 children who loved their six years by the coast.
            </li>
          </ol>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "We were young and there were two boys my juniors, one about one and a half years younger, Morris, and then Baxter, a couple of years younger than he, so that to all intents and purposes we were The Three Musketeers. Usually where you found one you found the three, be in hunting or dancing.
            </p>

            <p>
              We three boys and Pater worked as one unit, Pater pointing out that three slender sticks bound together could resist much more pressure than by each standing alone. We had the freedom of the Happy Valley hills to hunt wild pigs and we also had all the work we wanted, and altogether they were joyous days for youths with all those bush gullies to ramble in. Wild port was abundant, and the busy supplied us with all the honey we required and we saw to it that we had it.
            </p>

            <p>
              There was never a happier family in this world than we, with sufficient number in ourselves to form a nucleus for a party, should any girls happen in the vicinity. What rambles in the bush, and along the seashore, where the bathing was perfect, while the tide was low, but dangerous if full, as the writer well knows, due to one life and earth swim, to beat a strong current. Guns and dogs were ours, also the confidence of the Happy Valley overseer, and the years passed merrily along..."
            </p>
          </blockquote>
        </section>

        {/* Footnotes Section */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl">
          <div className="flex items-center gap-2 text-[#7a5b3a] font-serif font-bold text-sm uppercase tracking-wider">
            <Info className="w-4 h-4" />
            <span>Footnotes (Part 2)</span>
          </div>

          <ol className="space-y-2 text-xs sm:text-sm text-[#4a3525] leading-relaxed list-decimal list-inside pl-1">
            <li id="fn-p2-1" className="scroll-mt-24">
              Much of this information comes from R. C. Edward T. Roberts, grandson of R.M. & S. E. Roberts
            </li>
            <li id="fn-p2-2" className="scroll-mt-24">
              Netherby and Helvellyn
            </li>
            <li id="fn-p2-3" className="scroll-mt-24">
              From an essay Edward T. Roberts wrote as a schoolboy about his grandfather.
            </li>
            <li id="fn-p2-4" className="scroll-mt-24">
              This appeared in The Press, 6 December 1935
            </li>
            <li id="fn-p2-5" className="scroll-mt-24">
              Later renamed Champion Street
            </li>
            <li id="fn-p2-6" className="scroll-mt-24">
              T.E.L Roberts was the eldest son, and he later sold to his youngest brother, Frederick Roberts.
            </li>
            <li id="fn-p2-7" className="scroll-mt-24">
              The Bealey Street house was sited on what is now Southern Cross Hospital on Bealey Avenue
            </li>
            <li id="fn-p2-8" className="scroll-mt-24">
              Mr & Mrs Alexander of Kaiapoi
            </li>
            <li id="fn-p2-9" className="scroll-mt-24">
              Daughter of T.E.L. Roberts speaking at 1990 Family Reunion
            </li>
            <li id="fn-p2-10" className="scroll-mt-24">
              Page 44
            </li>
            <li id="fn-p2-11" className="scroll-mt-24">
              Old expression meaning to die
            </li>
            <li id="fn-p2-12" className="scroll-mt-24">
              From P R May's "The West Coast Gold Rushes (1967)
            </li>
            <li id="fn-p2-13" className="scroll-mt-24">
              Margaret was born 1822 in Ireland and died 20 July 1902 in Canterbury
            </li>
            <li id="fn-p2-14" className="scroll-mt-24">
              Jim Gibb was James Lindsay Gibb, later of Heathfied, married Lizzie Roberts
            </li>
            <li id="fn-p2-15" className="scroll-mt-24">
              RMR was only 65 at the time
            </li>
            <li id="fn-p2-16" className="scroll-mt-24">
              In 1907 there was a serious outbreak of influenza in NZ, more than 200 people died
            </li>
            <li id="fn-p2-17" className="scroll-mt-24">
              Christchurch International Exhibition 1906-7 held at Hagley Park
            </li>
            <li id="fn-p2-18" className="scroll-mt-24">
              Holmes A1 Temperance Hotel on Manchester Street
            </li>
            <li id="fn-p2-19" className="scroll-mt-24">
              Baxter, George & Bert were farming at Waitahuna (Otago) for a short time.
            </li>
            <li id="fn-p2-20" className="scroll-mt-24">
              Home was Plas Isa near Scargill. Robert & Sarah retired to Christchurch the following year, 1908
            </li>
            <li id="fn-p2-21" className="scroll-mt-24">
              His son in law, Sam Craighead, married to Fanny. They were in Mid Canterbury.
            </li>
            <li id="fn-p2-22" className="scroll-mt-24">
              RMR started his working life as an apprentice cabinet maker.
            </li>
            <li id="fn-p2-23" className="scroll-mt-24">
              George Gibb married Anna Alice Burgess in 1913, when he was 48; they had 2 sons.
            </li>
            <li id="fn-p2-24" className="scroll-mt-24">
              Walter Leach had a farm near Rotheram. The Roberts men used to stop for a yarn with him on their way to a farmlet they had, called Skibereen.
            </li>
            <li id="fn-p2-25" className="scroll-mt-24">
              The shearing shed boards where the sheep are fed in for the shearers - in larger places there might be 6-7 shearers on each board.
            </li>
            <li id="fn-p2-26" className="scroll-mt-24">
              Ted and Jennie Roberts were at Blind River, near Blenheim, living with Jennie's father, Mr Trueman. They won a farm near Culverden in 1908 ballot.
            </li>
            <li id="fn-p2-27" className="scroll-mt-24">
              Mrs Wachsman
            </li>
            <li id="fn-p2-28" className="scroll-mt-24">
              Waitahuna is 11 km SE of Lawrence, Otago
            </li>
            <li id="fn-p2-29" className="scroll-mt-24">
              Sister Mary Craighead
            </li>
            <li id="fn-p2-30" className="scroll-mt-24">
              T.E.L Roberts and his wife Crystabel, at Riverside near Culverden
            </li>
            <li id="fn-p2-31" className="scroll-mt-24">
              George was killed in a dray accident on his 28th birthday in 1914
            </li>
            <li id="fn-p2-32" className="scroll-mt-24">
              Bert was living at Culverden with Edward and his sister Margaret who was rearing Edward's infant son, R.C. Edward T. after Edward's wife Jennie died after childbirth in 1913.
            </li>
            <li id="fn-p2-33" className="scroll-mt-24">
              Tureiti Te Heuheu of Ngati Tuwharetoa
            </li>
            <li id="fn-p2-34" className="scroll-mt-24">
              Relevant summary in The Journal of Polynesian Society by G V Butterworth (Massey) A Rural Maori Renaissance? Maori Society and Politics 1920-1951"
            </li>
            <li id="fn-p2-35" className="scroll-mt-24">
              Bert helped with farm work and looked after the orchard and vegetable garden for the farm
            </li>
            <li id="fn-p2-36" className="scroll-mt-24">
              Frederick James Hayman was born in Somerset (UK) in 1860. He came to NZ on "Blue Jacket" with his family in 1864. He married Margaret Bunting in 1886. In 1900 he was appointed to teach at Burkes Pass Public School.
            </li>
            <li id="fn-p2-37" className="scroll-mt-24">
              Eliza Catherine, Mrs James Gibb, was also buried in Waikari Cemetery
            </li>
            <li id="fn-p2-38" className="scroll-mt-24">
              Lizzie 21 October 1869-23 February 1913
            </li>
            <li id="fn-p2-39" className="scroll-mt-24">
              Taken direct from "A Brief Record of Our Family"
            </li>
            <li id="fn-p2-40" className="scroll-mt-24">
              Irish Potato Famine 1845-1852 - about 1 mil people died and about the same number emigrated.
            </li>
            <li id="fn-p2-41" className="scroll-mt-24">
              Skeith, also called coulter, the part of the plough that cuts into the ground
            </li>
          </ol>
        </section>

        {/* Navigation Footer */}
        {onSelectPage && (
          <div className="pt-6 border-t border-[#d8ceb9] flex flex-wrap items-center justify-between gap-4">
            <button
              onClick={() => onSelectPage('part1')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-sm transition cursor-pointer"
            >
              <span>&larr; Back to Part 1: Wales</span>
            </button>

            <button
              onClick={() => onSelectPage('hub')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition shadow-xs cursor-pointer"
            >
              <Home className="w-4 h-4" />
              <span>Back to Roberts Family Hub</span>
            </button>
          </div>
        )}

      </article>
    </div>
  );
};
